// Şub 27, 2026 — Dashboard DPD uyarı widget'i
import { useState, useEffect } from 'react';
import api from '../../lib/api';
import { useTranslation } from '../../lib/i18n';
import { AlertTriangle, TrendingDown, PackageX, ChevronRight, Loader2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Badge } from '../ui/badge';

const SEVERITY_STYLES = {
  high: 'bg-red-50 border-red-300 text-red-800 dark:bg-red-950/40 dark:border-red-700 dark:text-red-200',
  medium: 'bg-orange-50 border-orange-300 text-orange-800 dark:bg-orange-950/40 dark:border-orange-700 dark:text-orange-200',
  low: 'bg-yellow-50 border-yellow-300 text-yellow-800 dark:bg-yellow-950/40 dark:border-yellow-700 dark:text-yellow-200',
};

export default function DPDAlertsWidget() {
  const { t } = useTranslation();
  const [firmId, setFirmId] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [collapsed, setCollapsed] = useState(false);

  useEffect(() => {
    api.get('/admin/firms').then((r) => {
      const list = r.data?.data || [];
      const ft = list.find((f) => /fast.?trans/i.test(f.name)) || list[0];
      if (ft) setFirmId(ft.id);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!firmId) return;
    setLoading(true);
    api.get(`/dpd/alerts?firm_id=${firmId}`)
      .then((r) => setData(r.data?.data))
      .catch(() => setData({ alerts: [], counts: {} }))
      .finally(() => setLoading(false));
  }, [firmId]);

  if (!firmId) return null;
  const alerts = data?.alerts || [];
  const counts = data?.counts || { high: 0, medium: 0, low: 0 };
  const totalCount = counts.high + counts.medium + counts.low;

  if (!loading && totalCount === 0) return null;

  return (
    <div
      className="bg-white dark:bg-slate-800/60 rounded-2xl border border-slate-200 dark:border-slate-700/50 overflow-hidden"
      data-testid="dpd-alerts-widget"
    >
      <button
        type="button"
        onClick={() => setCollapsed((c) => !c)}
        className="w-full flex items-center justify-between p-4 hover:bg-slate-50 dark:hover:bg-slate-800/50"
      >
        <div className="flex items-center gap-2">
          <AlertTriangle className="h-5 w-5 text-orange-600" />
          <h3 className="text-sm font-semibold" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {t('dpd.alerts.widgetTitle')}
            {data?.date && <span className="text-xs text-gray-500 ml-2">({data.date})</span>}
          </h3>
          {loading && <Loader2 className="h-3 w-3 animate-spin" />}
        </div>
        <div className="flex items-center gap-2">
          {counts.high > 0 && <Badge className="bg-red-600">{counts.high} {t('dpd.alerts.severityHigh')}</Badge>}
          {counts.medium > 0 && <Badge className="bg-orange-500">{counts.medium} {t('dpd.alerts.severityMedium')}</Badge>}
          {counts.low > 0 && <Badge className="bg-yellow-500">{counts.low} {t('dpd.alerts.severityLow')}</Badge>}
          <ChevronRight className={`h-4 w-4 text-gray-400 transition-transform ${collapsed ? '' : 'rotate-90'}`} />
        </div>
      </button>
      {!collapsed && (
        <div className="border-t divide-y">
          {alerts.slice(0, 10).map((a, i) => (
            <div
              key={i}
              className={`flex items-center gap-3 p-3 text-xs border-l-4 ${SEVERITY_STYLES[a.severity]}`}
              data-testid={`dpd-alert-${a.type}-${i}`}
            >
              {a.type === 'low_delivery_rate' ? (
                <TrendingDown className="h-4 w-4 flex-shrink-0" />
              ) : (
                <PackageX className="h-4 w-4 flex-shrink-0" />
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="text-[10px] uppercase">{t(`dpd.alerts.badges.${a.severity}`)}</Badge>
                  <span className="text-[10px] font-mono opacity-70">{a.type === 'low_delivery_rate' ? t('dpd.alerts.types.lowDelivery') : t('dpd.alerts.types.highRetour')}</span>
                </div>
                <p className="mt-0.5">{a.message}</p>
              </div>
              <Link
                to={a.type === 'low_delivery_rate' ? '/dashboard/dpd/consolidated' : '/dashboard/dpd/subco'}
                className="text-[11px] underline whitespace-nowrap"
              >
                {t('dpd.alerts.detailLink')}
              </Link>
            </div>
          ))}
          {alerts.length > 10 && (
            <div className="p-3 text-xs text-center text-gray-500">
              {t('dpd.alerts.moreAlerts', { n: alerts.length - 10 })}
              <Link to="/dashboard/dpd/consolidated" className="underline">DPD</Link>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
